import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchPatients, removePatient, searchPatients } from '../redux/slices/patientSlice';
import ConfirmationModal from './ConfirmationModal';
import '../styles/TableStyles.css';

function PatientList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { patients, loading, pagination } = useSelector((state) => state.patients);
  const doctorId = useSelector((state) => state.auth.doctor?.doctorId);
  const isAdmin = useSelector((state) => state.admin.isAuthenticated);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const tableContainerRef = useRef(null);
  const isFocusedRef = useRef(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const itemsPerPage = 10;

  React.useEffect(() => {
    // If admin, fetch all patients
    // If doctor, only fetch when doctorId is available
    console.log('PatientList - isAdmin:', isAdmin, 'doctorId:', doctorId);
    if (isAdmin) {
      dispatch(fetchPatients({ doctorId: null, page: currentPage, limit: itemsPerPage }));
    } else if (doctorId) {
      dispatch(fetchPatients({ doctorId, page: currentPage, limit: itemsPerPage }));
    }
  }, [dispatch, doctorId, isAdmin, currentPage]);

  // Maintain focus on search input while typing and after search results update
  useEffect(() => {
    if (inputRef.current && isFocusedRef.current) {
      inputRef.current.focus();
    }
  }, [searchQuery, patients]);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear previous timeout
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timeout for debounced search
    debounceTimerRef.current = setTimeout(() => {
      const idToPass = isAdmin ? null : doctorId;
      if (query.trim()) {
        dispatch(searchPatients({ query, doctorId: idToPass }));
      } else {
        setCurrentPage(1);
        dispatch(fetchPatients({ doctorId: idToPass, page: 1, limit: itemsPerPage }));
      }
    }, 800); // 800ms debounce - gives user more time to type
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePatientId, setDeletePatientId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (e, id) => {
    e.stopPropagation();
    setDeletePatientId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    setIsDeleting(true);
    dispatch(removePatient(deletePatientId)).then(() => {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDeletePatientId(null);
    }).catch(() => {
      setIsDeleting(false);
    });
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeletePatientId(null);
  };

  const handleRowClick = (patient) => {
    navigate(`/patient/${patient.patient_id}`, { state: { patient } });
  };

  const handleNextPage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentPage < pagination.pages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="table-container" ref={tableContainerRef}>
      <div className="search-container">
        <input
          ref={inputRef}
          type="text"
          placeholder="Search by name, email, phone, or NID..."
          value={searchQuery}
          onChange={handleSearch}
          onFocus={() => { isFocusedRef.current = true; }}
          onBlur={() => { isFocusedRef.current = false; }}
          className="search-input"
        />
        {patients.length > 0 && (
          <div className="pagination-controls">
            <button 
              onClick={handlePrevPage} 
              disabled={currentPage === 1}
              className="pagination-btn"
              type="button"
              title="Previous page"
            >
              &lt;
            </button>
            <span className="pagination-info">
              {pagination.page}/{pagination.pages}
            </span>
            <button 
              onClick={handleNextPage} 
              disabled={currentPage >= pagination.pages}
              className="pagination-btn"
              type="button"
              title="Next page"
            >
              &gt;
            </button>
          </div>
        )}
      </div>
      {patients.length === 0 ? (
        <p className="no-data">{inputRef.current?.value ? 'No patients found' : 'No patients found'}</p>
      ) : (
        <>
          {/* Top Table and Pagination */}

          <table className="modern-table">
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Landline</th>
                <th>NID Number</th>
                <th>Address</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {patients.map((patient) => (
              <tr 
                key={patient.patient_id}
                className="clickable-row"
                onClick={() => handleRowClick(patient)}
              >
                <td>{patient.first_name}</td>
                <td>{patient.last_name}</td>
                <td>{patient.email}</td>
                <td>{patient.phone_number || '-'}</td>
                <td>{patient.landline_number || '-'}</td>
                <td>{patient.nid_number || '-'}</td>
                <td>{patient.address || '-'}</td>
                <td className="action-buttons" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="delete-icon-btn"
                    onClick={(e) => handleDeleteClick(e, patient.patient_id)}
                    title="Delete patient"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Bottom Pagination Controls */}
        <div className="pagination-controls">
          <button 
            onClick={handlePrevPage} 
            disabled={currentPage === 1}
            className="pagination-btn"
            type="button"
            title="Previous page"
          >
            &lt;
          </button>
          <span className="pagination-info">
            {pagination.page}/{pagination.pages}
          </span>
          <button 
            onClick={handleNextPage} 
            disabled={currentPage >= pagination.pages}
            className="pagination-btn"
            type="button"
            title="Next page"
          >
            &gt;
          </button>
        </div>
        </>
      )}
      
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Patient"
        message="Are you sure you want to delete this patient? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
        isDangerous={true}
      />
    </div>
  );
}

export default PatientList;
